"use client";

import { MouseEventHandler, ReactNode, useCallback } from "react";

import {
  Portal,
  type DialogRootProps,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { Button, type IButtonProps, ButtonGroup } from "../button/button";
import { Dialog } from "../chakra-slots";

export interface ModalProps extends Omit<
  DialogRootProps,
  "open" | "onOpenChange" | "children"
> {
  open?: boolean;
  onClose?: () => void;
  centered?: boolean;
  scrollBehavior?: string;
  children?: ReactNode;
}

// Padding lives on the header/body/footer here, so flush the slot recipe's.
const flushDialogSlotPadding = {
  p: 0,
  px: 0,
  py: 0,
  pt: 0,
  pb: 0,
  pl: 0,
  pr: 0
};

export interface ModalContentProps extends HTMLChakraProps<"section"> {}

interface TemplateModalProps extends Pick<ModalProps, "open" | "onClose"> {
  header: string;
  body: ReactNode;
  buttonPrimary?: string;
  buttonSecondary?: string;
  buttonPrimaryVariant?: string;
  buttonSecondaryVariant?: string;
  fullSizedModal?: boolean;
  primaryDestructive?: boolean;
  fullWidthButtons?: boolean;
  onPrimaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
  onSecondaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
}

function TemplateModal(props: TemplateModalProps) {
  const {
    open,
    onClose,
    header,
    body,
    buttonPrimary,
    buttonSecondary,
    buttonPrimaryVariant,
    buttonSecondaryVariant,
    onPrimaryClickHandler,
    onSecondaryClickHandler,
    fullSizedModal,
    fullWidthButtons,
    primaryDestructive,
    ...rest
  } = props;

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        onClose?.();
      }
    },
    [onClose]
  );

  const ButtonHandler = ({
    children,
    variant,
    destructive,
    onClick
  }: IButtonProps): JSX.Element => {
    return (
      <Button
        variant={variant}
        destructive={destructive}
        onClick={onClick}
        size={{ base: "lg", xl: "md" }}
        px={6}
        py={3}
        // Pin w:100% when full-width: v3 ButtonGroup centers (not stretches)
        // children, so buttons wouldn't fill the column on md+ otherwise.
        w={fullWidthButtons ? "100%" : { base: "100%", md: "auto" }}
        minW={{ base: "108px !important", md: "120px !important" }}
        maxW={fullWidthButtons ? "full" : 40}
      >
        {children}
      </Button>
    );
  };

  const modalSizeProps: ModalContentProps = {
    maxW: fullSizedModal ? undefined : 448,
    minH: fullSizedModal ? undefined : 248,
    maxH: { base: "calc(100% - 2rem)", lg: "calc(100% - 5rem)" },
    m: fullSizedModal ? 0 : 4,
    ...rest
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={handleOpenChange}
      placement="center"
      size={fullSizedModal ? "full" : "md"}
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            p={6}
            gap={6}
            minW={288}
            boxShadow="lg"
            {...modalSizeProps}
          >
            <Dialog.Header
              {...flushDialogSlotPadding}
              display="flex"
              fontSize="lg"
              alignItems="flex-start"
              alignSelf="stretch"
              justifyContent="space-between"
            >
              {header}
              <Dialog.CloseTrigger position="unset" w={6} h={6} />
            </Dialog.Header>
            <Dialog.Body {...flushDialogSlotPadding} fontSize="md">
              {body}
            </Dialog.Body>
            <Dialog.Footer
              {...flushDialogSlotPadding}
              alignItems="flex-start"
              gap={6}
              alignSelf="stretch"
              justifyContent={{ base: "space-between", sm: "flex-end" }}
            >
              <ButtonGroup
                // Constant gap: v3 dropped `spacing`, and gap:0 under
                // fullWidthButtons collapses the space between stacked buttons.
                gap={4}
                justifyContent={!fullWidthButtons ? "flex-end" : "center"}
                width={fullWidthButtons ? "full" : "auto"}
                flexDirection={fullWidthButtons ? "column" : "row-reverse"}
              >
                {buttonPrimary && (
                  <ButtonHandler
                    variant={buttonPrimaryVariant || "primary"}
                    destructive={primaryDestructive}
                    onClick={onPrimaryClickHandler}
                  >
                    {buttonPrimary}
                  </ButtonHandler>
                )}
                {buttonSecondary && (
                  <ButtonHandler
                    variant={buttonSecondaryVariant || "secondary"}
                    onClick={onSecondaryClickHandler}
                  >
                    {buttonSecondary}
                  </ButtonHandler>
                )}
              </ButtonGroup>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export { TemplateModal };
export type { TemplateModalProps };
